import { useTableFilter } from "@/shared";
import { GeneralTable } from "@/shared/components/ui";
import DateRangePicker from "@/shared/components/ui/CustomSearch/DateRangePicker";
import {
  Grid,
  MenuItem,
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import Flatpickr from "react-flatpickr";
import { Description, PictureAsPdf, Search } from "@mui/icons-material";
import { useObtenerReporteContratosPendientes } from "@/shared/actions/erp";
import { useTableColumnsReporteContratosPendientes } from "@/shared/hooks/useTableColumnsReporteContratosPendientes";
import { format } from "date-fns";
import { exportToExcel, ExportColumn } from "../utils/exportUtils";

export default function TableReporteContratosPendientes() {
  const {
    dateBefore,
    dateAfter,
    onChangeRangeDate,
    vendedor,
  } = useTableFilter();

  // Estado de filtros
  const [estadoContratoFilter, setEstadoContratoFilter] = useState(["PENDIENTE"]);

  // Estados de datos y carga
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [allowedDateRange, setAllowedDateRange] = useState<{ start: string; end: string } | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string>("");

  // Estados para modal de exportación
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportType, setExportType] = useState<"excel" | "pdf" | "">("");
  const [exportProgress, setExportProgress] = useState<number>(0);

  // Paginación para Material React Table (manejada internamente por la tabla)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Calcular fechas del mes actual
  const { firstDayCurrentMonth, currentDay } = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    // Primer día del mes actual
    // (JavaScript cuenta los meses desde 0, así que 'month' es el mes actual)
    const firstDay = new Date(year, month, 1);

    return {
      firstDayCurrentMonth: format(firstDay, "yyyy-MM-dd"),
      currentDay: format(today, "yyyy-MM-dd")
    };
  }, []);

  // AbortController para cancelar requests anteriores
  const abortControllerRef = useRef<AbortController | null>(null);

  // Hook para obtener datos
  const obtenerReporteMutation = useObtenerReporteContratosPendientes();


  // Cargar datos con filtros
  const loadData = useCallback(async () => {
    // Cancelar request anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError("");

    try {
      const filters: any = {
        fecha_desde: dateAfter || firstDayCurrentMonth,
        fecha_hasta: dateBefore || currentDay,
      };

      if (vendedor) filters.id_usuario = parseInt(vendedor);

      const response = await obtenerReporteMutation.mutateAsync(filters);

      // Verificar si fue abortado
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (response.data && Array.isArray(response.data)) {
        setAllData(response.data);
        // Resetear a la primera página cuando se cargan nuevos datos
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      } else {
        setError("Estructura de respuesta inesperada");
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return; // Request cancelado, no mostrar error
      }

      // Manejar error de rango de fechas inválido
      if (err?.response?.data?.code === 'INVALID_DATE_RANGE') {
        const allowed = err.response.data.allowed_range;
        setAllowedDateRange(allowed);
        setError(err.response.data.message || "Rango de fechas inválido");
      } else {
        setError(err?.message || "Error al cargar datos");
      }
    } finally {
      setIsLoading(false);
    }
  }, [dateAfter, dateBefore, vendedor, firstDayCurrentMonth, currentDay]);

  // Cargar datos al montar (con rango del mes anterior)
  useEffect(() => {
    if (!dateAfter && !dateBefore) {
      // Establecer rango del mes anterior por defecto
      const startDate = new Date(firstDayCurrentMonth);
      const endDate = new Date(currentDay);
      onChangeRangeDate([startDate, endDate]);
    }
  }, []);

  useEffect(() => {
    // Si no hay nada seleccionado, seleccionar PENDIENTE por defecto
    if (estadoContratoFilter.length === 0) {
      setEstadoContratoFilter(["PENDIENTE"]);
    }
  }, []); // Se ejecuta solo al montar el componente

  // Validar rango de fechas
  useEffect(() => {
    if (!dateAfter || !dateBefore) {
      setDateRangeError("");
      return;
    }

    const fechaDesde = new Date(dateAfter);
    const fechaHasta = new Date(dateBefore);
    const diffDays = Math.ceil((fechaHasta.getTime() - fechaDesde.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays > 31) {
      setDateRangeError(`El rango seleccionado es de ${diffDays} días. Solo se permiten hasta 31 días.`);
      setAllData([]);
      return;
    } else {
      setDateRangeError("");
    }
  }, [dateAfter, dateBefore]);

  const { columnBase } = useTableColumnsReporteContratosPendientes({ pagination });
  const flatpickrRef = useRef<Flatpickr>(null);

  const filteredData = useMemo(() => {
    let data = allData;
    return data;
  }, [allData]);

  const exportFilters = useMemo(() => {
    const filters: Record<string, string> = {};

    if (dateAfter && dateBefore) {
      filters["Rango de fechas"] = `${dateAfter} al ${dateBefore}`;
    }

    if (estadoContratoFilter.length > 0) {
      filters["Tipo de plan"] = estadoContratoFilter.join(", ");
    }

    return filters;
  }, [
    dateAfter,
    dateBefore,
    estadoContratoFilter,
  ]);

  // Paginación en memoria optimizada - solo pasamos las filas visibles a la tabla
  // Esto mejora drásticamente el rendimiento porque GeneralTable solo renderiza 10-50 filas en vez de 4812
  const paginatedData = useMemo(() => {
    const startIdx = pagination.pageIndex * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    return filteredData.slice(startIdx, endIdx);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleSearch = async () => {
    await loadData();
  };

  // Exportar a Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportType("excel");
    setExportProgress(0);

    try {
      // Simulación de progreso
      setExportProgress(30);

      const columns: ExportColumn[] = [
        { header: "Contrato #", key: "idContrato" },
        { header: "Contrato Eva (Ref)", key: "instalacion.numero_referencia" },
        { header: "Fecha Instalación", key: "fecha_instalacion" },
        { header: "Fecha Subida Contrato", key: "fecha_subida" },
        { header: "Documento", key: "instalacion.cedula_identidad" },
        { header: "Cliente", key: "nombre_cliente" },
        { header: "Estado del Servicio SOFTNET", key: "estado_servicio" },
        { header: "Estado del contrato SOFTNET", key: "estadocontrato.descripcion" },
        { header: "Ciudad", key: "ciudad.Ciudad" },
        { header: "Tecnico", key: "tecnico.nombre_tecnico" },
        { header: "Detalle Pago", key: "instalacion.costo_instalacion" },
        { header: "Vendedor", key: "vendedor" },
        { header: "Area", key: "instalacion.vendedor.canalventa.descripcion" },
        { header: "Observación Técnico", key: "instalacion.contrato_eva.observaciones_eva.observacion_tecnico" },
        { header: "Observación Ventas", key: "instalacion.contrato_eva.observaciones_eva.observacion_ventas" },
        { header: "Estado Contrato EVA", key: "instalacion.contrato_eva.estado_actualizacion" },
        { header: "Fecha Última Actualización", key: "instalacion.contrato_eva.fecha_modificacion" },
      ];

      setExportProgress(60);

      await new Promise(resolve => setTimeout(resolve, 500)); // Dar tiempo para mostrar progreso

      exportToExcel(filteredData, columns, { filters: exportFilters });

      setExportProgress(100);

      // Esperar un momento antes de cerrar el modal
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      setError("Error al exportar a Excel");
    } finally {
      setIsExporting(false);
      setExportType("");
      setExportProgress(0);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info" sx={{ bgcolor: "info.light" }}>
          <Typography variant="body2">
            <strong>Información:</strong> Se mostrarán datos del ultimo mes.
            Se permite obtener un rango máximo de 31 días por consulta.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Nota:</strong> Solo se muestran los contratos con estado de contrato "PENDIENTE DE REVISIÓN".
          </Typography>
        </Alert>
      </Grid>

      {/* Error de rango de fechas */}
      {dateRangeError && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setDateRangeError("")}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dateRangeError}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Por favor, ajuste el rango de fechas a máximo 31 días.
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Error con rango permitido */}
      {error && allowedDateRange && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {error}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Rango permitido: {allowedDateRange.start} a {allowedDateRange.end}
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Error genérico */}
      {error && !allowedDateRange && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Grid>
      )}

      {/* Indicador de carga */}
      {isLoading && (
        <Grid item xs={12}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Cargando datos...
            </Typography>
            <LinearProgress />
          </Box>
        </Grid>
      )}

      {/* Filtros principales */}
      <Grid item xs={12}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DateRangePicker
              size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              flatpickrRef={flatpickrRef}
              onChange={(range) => onChangeRangeDate(range)}
              label="Rango de fechas"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth size="small" required>
              <InputLabel required>Estado Contrato</InputLabel>
              <Select
                value={estadoContratoFilter[0] || "PENDIENTE"}
                onChange={(e) => setEstadoContratoFilter([e.target.value])}
                label="Estado Contrato"
                required
              >
                <MenuItem value="PENDIENTE">
                  Pendiente
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      {/* Botones de acción */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={dateRangeError !== ""}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Consultar
          </Button>

          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

          <Button
            variant="contained"
            color="success"
            size="medium"
            startIcon={<Description />}
            onClick={handleExportExcel}
            disabled={allData.length === 0}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Excel
          </Button>
        </Box>
      </Grid>

      {/* Información de registros */}
      {!isLoading && allData.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ fontWeight: 500 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {filteredData.length === allData.length ? (
                `Total de registros encontrados: ${allData.length.toLocaleString()}`
              ) : (
                `Mostrando ${filteredData.length.toLocaleString()} de ${allData.length.toLocaleString()} registros`
              )}
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Tabla */}
      <Grid item xs={12}>
        <GeneralTable
          columns={columnBase}
          data={paginatedData}
          isLoading={isLoading}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={filteredData.length}
          enableColumnActions={false}
          enableEditing={false}
          enableGlobalFilter={false}
          enableColumnFilters={false}
        />
      </Grid>

      {/* Modal de progreso de exportación */}
      <Dialog
        open={isExporting}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            minWidth: 400,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {exportType === "excel" ? (
              <Description sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exportando {exportType === "excel" ? "Excel" : "PDF"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {exportProgress < 50 ? "Preparando datos..." :
                  exportProgress < 80 ? "Generando archivo..." :
                    "Finalizando descarga..."}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {exportProgress}% completado
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              Por favor espere, esto puede tomar unos segundos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
